import { FeatureFlags, RESOURCE_TO_FLAG_MAP } from '../constants/featureFlags';
import logger from '../utils/logger';
// Global reference to GrowthBook instance (set by provider)
let growthBookInstance = null;

/**
 * Set the GrowthBook instance for API URL resolution
 * Called by GrowthBookProvider on initialization
 * @param {GrowthBook} instance - GrowthBook instance
 */
export const setGrowthBookInstance = (instance) => {
  growthBookInstance = instance;
};

/**
 * Get API URL for a resource based on feature flags
 * This is a utility function (not a hook) that can be used in data providers
 * @param {string} resourceName - Resource name from RESOURCE_NAME constant
 * @returns {string} API base URL (V1 or V2)
 */
export const getApiUrlForResource = (resourceName) => {
  // Environment URLs
  const V1_API_URL = import.meta.env.VITE_API_URL;
  const V2_API_URL = import.meta.env.VITE_API_V2_URL;


  // If GrowthBook isn't initialized yet, default to V1
  if (!growthBookInstance) {
    logger.warn(`[API URL Resolver] GrowthBook not initialized, defaulting to V1 for ${resourceName}`);
    return V1_API_URL;
  }

  // Special case: partner-marketing-assets only exists in V2, so always use V2
  // This check must come before the global override to prevent breaking this resource
  if (resourceName === 'partner-marketing-assets') {
    logger.log(`[API URL Resolver] partner-marketing-assets only exists in V2, using V2`);
    return V2_API_URL;
  }

  // Check global override (emergency rollback)
  const globalOverride = growthBookInstance.isOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_GLOBAL_OVERRIDE);
  if (globalOverride) {
    const debugMode = growthBookInstance.isOn(FeatureFlags.ENABLE_ADMIN_PANEL_API_DEBUG_MODE);
    if (debugMode) {
      logger.log(`[API URL Resolver] Global override: using V1 for ${resourceName}`);
    }
    return V1_API_URL;
  }

  // Get resource-specific flag
  const flagName = RESOURCE_TO_FLAG_MAP[resourceName];

  if (!flagName) {
    logger.warn(`[API URL Resolver] No feature flag defined for resource: ${resourceName}, defaulting to V1`);
    return V1_API_URL;
  }

  // Check if V2 is enabled for this resource
  let useV2 = growthBookInstance.isOn(flagName);


  const selectedUrl = useV2 ? V2_API_URL : V1_API_URL;


  // Debug logging if enabled
  const debugMode = growthBookInstance.isOn(FeatureFlags.ENABLE_ADMIN_PANEL_API_DEBUG_MODE);
  if (debugMode) {
    logger.log(`[API URL Resolver] ${resourceName}: using ${useV2 ? 'V2' : 'V1'} - ${selectedUrl}`);
  }

  return selectedUrl;
};

/**
 * Get API version for a resource (for monitoring/analytics)
 * @param {string} resourceName - Resource name from RESOURCE_NAME constant
 * @returns {string} 'v1' or 'v2'
 */
export const getApiVersionForResource = (resourceName) => {
  if (!growthBookInstance) return 'v1';

  // Special case: partner-marketing-assets only exists in V2
  if (resourceName === 'partner-marketing-assets') return 'v2';

  const globalOverride = growthBookInstance.isOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_GLOBAL_OVERRIDE);
  if (globalOverride) return 'v1';

  const flagName = RESOURCE_TO_FLAG_MAP[resourceName];
  if (!flagName) return 'v1';

  return growthBookInstance.isOn(flagName) ? 'v2' : 'v1';
};

/**
 * Helper function to map common resource names to our flag mapping
 * Some data providers might use slightly different naming
 */
const normalizeResourceName = (resourceName) => {
  const mappings = {
    // Handle any naming variations
    'user': 'users',
    'role': 'roles',
    'organization': 'organizations',
    'code': 'codes',
    'queue': 'queues',
    'version': 'versions',
    'cms1500Job': 'cms1500Jobs',
    'leadImport': 'leadImports',
    'state': 'states',
    'permissionTemplate': 'permissionTemplates',
    'impersonation': 'impersonation',
    'lead': 'leads',  // single lead maps to leads resource
  };

  return mappings[resourceName] || resourceName;
};

/**
 * Enhanced version that normalizes resource names
 * @param {string} resourceName - Resource name (may need normalization)
 * @returns {string} API base URL
 */
export const getApiUrl = (resourceName) => {
  const normalizedName = normalizeResourceName(resourceName);
  return getApiUrlForResource(normalizedName);
};
